import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Share2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { COLORS, LINE_NAMES } from '../constants';
import { JAR_TYPES } from './three/jarMeta';
import { KitJar3D } from './KitJar3D';
import { useKitJarImages } from '../hooks/useKitJarImages';

const STAGE_SIZES = [80, 94, 110, 94, 80];

const KitPreviewModal = ({ kit, rank, items, fn, onClose, onBuy, onShare }) => {
  const { t } = useLanguage();
  const jars = useKitJarImages(items);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-[60] flex items-center justify-center p-4" onClick={onClose} data-testid="kit-preview-modal">
      <motion.div
        className="kp-modal"
        onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, y: 24, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      >
        <button onClick={onClose} className="share-close" aria-label={t('share.close')} data-testid="kit-preview-close">✕</button>

        <div className="kp-stage" data-testid="kit-preview-stage">
          {items.map((it, i) => (
            <KitJar3D key={`${it.product_type}-${i}`} src={jars[i]} line={it.line_color} size={STAGE_SIZES[i] || 84} />
          ))}
        </div>

        <div className="kp-body">
          <span className="eyebrow">{t('beautykit.eyebrow')}{rank ? ` · #${rank}` : ''}</span>
          <h3 className="share-title text-gradient-brand" data-testid="kit-preview-name">{kit.name}</h3>
          {fn && <span className="bk-fn text-gradient-brand" data-testid="kit-preview-fn">{fn}</span>}
          <p className="bk-by">{t('beautykit.by')} <strong>{kit.creator_name}</strong> · {kit.purchase_count} {t('beautykit.sold')}</p>

          <p className="kp-sub">{t('beautykit.preview_sub')}</p>
          <ul className="kp-list" data-testid="kit-preview-items">
            {items.map((it, i) => (
              <li key={`${it.product_type}-${i}`} className="kp-row" style={{ '--c': COLORS[it.line_color] || '#fff' }} data-testid={`kit-preview-item-${it.product_type}`}>
                <KitJar3D src={jars[i]} line={it.line_color} size={46} />
                <div className="kp-row-text">
                  <span className="kp-row-type">{t(JAR_TYPES[it.product_type]?.nameKey || '')} · {LINE_NAMES[it.line_color] || ''}</span>
                  <strong className="kp-row-ing">{it.main_ingredient}</strong>
                </div>
                <span className="kp-row-eff">{it.effect}</span>
              </li>
            ))}
          </ul>
          <span className="kp-volume" data-testid="kit-preview-volume">{t('home.mix.volume')}</span>

          <div className="kp-foot">
            <span className="bk-price" data-testid="kit-preview-price">€{kit.total_price?.toFixed(2) || '0.00'}</span>
            <div className="bk-foot-actions">
              <button onClick={() => onShare(kit)} className="bk-share" aria-label={t('beautykit.share')} title={t('beautykit.share')} data-testid="kit-preview-share">
                <Share2 size={17} />
              </button>
              <button onClick={() => { onBuy(kit); onClose(); }} className="btn-pill btn-pill-primary bk-buy" data-testid="kit-preview-buy">
                {t('beautykit.buy')}
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default KitPreviewModal;
